import React from 'react';
import { PremiumLock } from '@cx-web/components/primitives/icons';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import { useAuthModal, VIEW_LOGIN } from '@cx-web/feature-myoc-login-2018/provider';
import { trackEvent } from '@cx-web/utils/dataLayer';
import { formatInTimeZone } from 'date-fns-tz';

import { type Repub, REPUBS } from '@cx-modules/constants';
import { type DeviceType } from '@cx-modules/types';

import styles from './ArticleHeader.module.css';
import { getWebPImageUrlWithWidth } from './getWebPImageUrlWithWidth';
type ArticleHeaderImage = {
  alt: string;
  url: string;
};
type ArticleHeaderAuthor = {
  fullName: string;
  id: string;
  image: ArticleHeaderImage;
  shortName: string;
};
export type ArticleHeaderProps = {
  aiGeneratedId?: string;
  authors: ArticleHeaderAuthor[];
  componentTags?: string[];
  currentPathWithRepub: string;
  description?: string;
  deviceType: DeviceType;
  image: ArticleHeaderImage;
  myocData: MyOCData;
  publishedTime?: string;
  readTime?: string;
  repub: Repub;
  title: string;
};

export const ArticleHeader = ({
  aiGeneratedId,
  authors,
  componentTags,
  description,
  deviceType,
  image,
  myocData,
  publishedTime,
  readTime,
  repub,
  title,
}: ArticleHeaderProps) => {
  const { openAtView } = useAuthModal();
  const author = authors[0];
  const publishedTimeLocalised =
    publishedTime &&
    formatInTimeZone(
      publishedTime,
      REPUBS[repub].dateProperties.timezone,
      REPUBS[repub].dateProperties.format.timeDateFull,
      {
        locale: REPUBS[repub].dateProperties.locale,
      },
    );

  const isLoggedIn = !!myocData.myOCUser;

  const isMobile = deviceType === 'MOBILE';
  const imageWidth = isMobile ? 400 : 1000;

  const renderLoginBanner = componentTags?.includes('TIPS_PREMIUM');

  React.useEffect(() => {
    trackEvent('article_view', {
      tipsterName: author.fullName,
      articleName: title,
      isLocked: renderLoginBanner ? 'yes' : 'no',
      aiArticle: aiGeneratedId ? 'yes' : 'no',
    });
  }, [author.fullName, aiGeneratedId, renderLoginBanner, title]);

  return (
    <header
      aria-labelledby="article-title"
      className={styles.articleHeaderContainer}
      data-testid="article-header-container"
    >
      <picture className={styles.articleHeroImage}>
        <img
          alt={image.alt}
          className="w-full h-full absolute object-cover object-top"
          data-testid="article-hero-image"
          src={getWebPImageUrlWithWidth(image.url, imageWidth)}
        />
      </picture>
      <h1 className={styles.articleHeader} data-testid="article-title">
        {title}
      </h1>
      <h2 className={styles.articleSummary} data-testid="article-subtitle">
        {description}
      </h2>

      {renderLoginBanner && (
        <div className={styles.lockedContentIcon}>
          {!isLoggedIn ? (
            <button
              className={styles.lockedContentButton}
              onClick={() => {
                trackEvent('article_header_login', {
                  tipsterName: author.fullName,
                  articleName: title,
                });
                openAtView(VIEW_LOGIN);
              }}
              type="button"
            >
              log in to view for free
              <PremiumLock />
            </button>
          ) : (
            <>unlocked</>
          )}
        </div>
      )}
      <div className={styles.articleDetailsContainer}>
        <p className={styles.articleAuthor} data-testid="article-author-name">
          {author.fullName}
          {publishedTimeLocalised && ` - ${publishedTimeLocalised}`}
        </p>
        {readTime && (
          <span className={styles.articleMetadata} data-testid="article-metadata-read-time">
            {readTime}
          </span>
        )}
      </div>
    </header>
  );
};
